import { useLayoutEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/joy";
import "../../lib/pdfWorkerSetup";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

/**
 * Use the Documents gallery's PDF.js approach on the authenticated blob URL.
 * The deployed frame-src policy blocks blob URLs in native PDF iframes.
 */
export function PhotoReportPreview({ url }: { url: string }) {
  const container = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useLayoutEffect(() => {
    const element = container.current;
    if (!element) return;
    const measure = () =>
      setWidth(Math.max(1, Math.min(1200, element.clientWidth - 32)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const loading = (
    <Box role="status" sx={{ p: 3, display: "flex", justifyContent: "center", gap: 2 }}>
      <CircularProgress size="sm" />
      <Typography>Loading PDF preview…</Typography>
    </Box>
  );

  return (
    <Box
      ref={container}
      role="region"
      aria-label="Photo report PDF preview"
      tabIndex={0}
      sx={{
        flex: 1,
        minHeight: 200,
        minWidth: 0,
        overflow: "auto",
        bgcolor: "background.level2",
        borderRadius: "sm",
        p: 2,
        "& .react-pdf__Document": {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        },
      }}
    >
      {failed ? (
        <Alert color="danger" sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Box sx={{ flex: 1 }}>
            <Typography level="title-sm">Unable to display the PDF preview.</Typography>
            <Typography level="body-sm">You can still download the generated PDF below.</Typography>
          </Box>
          <Button
            variant="soft"
            color="danger"
            onClick={() => {
              setFailed(false);
              setNumPages(0);
              setAttempt((value) => value + 1);
            }}
          >
            Retry preview
          </Button>
        </Alert>
      ) : (
        <Document
          key={`${url}:${attempt}`}
          file={url}
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
          onLoadError={() => setFailed(true)}
          onSourceError={() => setFailed(true)}
          loading={loading}
          error=""
        >
          {width > 0 && Array.from({ length: numPages }, (_, index) => (
            <Box key={index + 1}>
              <Typography level="body-sm" sx={{ textAlign: "center", mb: 1 }}>
                Page {index + 1} of {numPages}
              </Typography>
              <Page
                pageNumber={index + 1}
                width={width}
                renderTextLayer
                renderAnnotationLayer
                loading={loading}
                error=""
                onLoadError={() => setFailed(true)}
                onRenderError={() => setFailed(true)}
              />
            </Box>
          ))}
        </Document>
      )}
    </Box>
  );
}
